import { useState, useEffect, useRef } from 'react';

import { ATL_GENERAL } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { usePageState } from '@confluence/page-context';
import { expVal } from '@confluence/feature-experiments';

import { useWebPanelLocation } from './useWebPanelLocation.ts';

declare global {
	interface Window {
		__ATL_GENERAL_WEBPANEL_HEIGHT_CHANGES__?: number[];
	}
}

export const useATLGeneralWebPanelListener = () => {
	const atlGeneralHeightChanges = useRef<number[]>([]);

	const [webPanelHeight, setWebPanelHeight] = useState(() => {
		if (
			typeof window !== 'undefined' &&
			Boolean(window.__SSR_RENDERED__) &&
			expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false)
		) {
			const initialHeight = window['__ATL_GENERAL_WEBPANEL_INITIAL_HEIGHT__'];
			return initialHeight ?? 0;
		} else {
			return getAtlGeneralWebPanelHeight();
		}
	});
	const [{ contentId, spaceKey }] = usePageState();

	const { panels } = useWebPanelLocation({
		location: ATL_GENERAL,
		fetchPolicy: 'cache-first',
		spaceKey,
		contentId,
	});

	const isATLGeneralWebPanelFixed = panels.length > 0;

	useEffect(() => {
		const listener = (e) => {
			let newHeight: number;

			if (expVal('confluence_p2m_style_recalc_and_expand_joint_exp', 'isEnabled', false)) {
				setWebPanelHeight(e?.detail?.height ?? 0);
				newHeight = e?.detail?.height ?? 0;
			} else {
				setWebPanelHeight(getAtlGeneralWebPanelHeight());
				newHeight = getAtlGeneralWebPanelHeight();
			}

			// Track ATL General Webpanel height changes for page initial load
			if (typeof window !== 'undefined' && Boolean(window.__SSR_RENDERED__)) {
				// Last height is the SSR rendered height or last observed height
				const lastHeight =
					atlGeneralHeightChanges.current.length === 0
						? window['__ATL_GENERAL_WEBPANEL_INITIAL_HEIGHT__'] || 0
						: atlGeneralHeightChanges.current[atlGeneralHeightChanges.current.length - 1];

				// If the last height is not the same as the new height, track the change
				if (lastHeight !== newHeight) {
					atlGeneralHeightChanges.current.push(newHeight);
				}
				window['__ATL_GENERAL_WEBPANEL_HEIGHT_CHANGES__'] = atlGeneralHeightChanges.current;
			}
		};

		if (isATLGeneralWebPanelFixed) {
			document.addEventListener('atlGeneralWebPanelHeightUpdated', listener);
		}

		return () => {
			if (isATLGeneralWebPanelFixed) {
				document.removeEventListener('atlGeneralWebPanelHeightUpdated', listener);
			}
		};
	}, [isATLGeneralWebPanelFixed]);

	return {
		isATLGeneralWebPanelFixed,
		atlGeneralWebPanelHeight: webPanelHeight,
	};
};

const getAtlGeneralWebPanelHeight = () => {
	if (typeof document === 'undefined') {
		return 0;
	}

	return (
		parseInt(
			getComputedStyle(document.documentElement).getPropertyValue('--atl-general-webpanel-height'),
		) || 0
	);
};
